import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TenantService } from '../../../services/tenant.service';
import { TenantDocumentService, TenantDocument } from '../../../services/tenant-document.service';
import { ToastService } from '../../../services/toast.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { Tenant, UpdateTenantRequest } from '../../../models/tenant.model';
import { MaterialModule } from '../../../shared/material.module';

export interface TenantViewModalData {
  tenant: Tenant;
}

@Component({
  selector: 'app-tenant-view-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './tenant-view-modal.component.html',
  styleUrl: './tenant-view-modal.component.scss'
})
export class TenantViewModalComponent implements OnInit {
  tenantForm: FormGroup;
  isLoading = false;
  isEditing = false;
  selectedTabIndex = 0;
  documents: TenantDocument[] = [];
  loadingDocuments = false;

  // Mock documents data for fallback
  mockDocuments: TenantDocument[] = [
    {
      id: '1',
      name: 'Tenant Agreement.pdf',
      originalName: 'Tenant Agreement.pdf',
      type: 'Contract',
      mimeType: 'application/pdf',
      size: 2457600,
      filePath: '/uploads/tenant-agreement.pdf',
      uploadedBy: 'Admin User',
      isActive: true,
      tenantId: '',
      createdAt: new Date('2024-01-15'),
      updatedAt: new Date('2024-01-15')
    },
    {
      id: '2',
      name: 'License Certificate.pdf',
      originalName: 'License Certificate.pdf',
      type: 'License',
      mimeType: 'application/pdf',
      size: 1887436,
      filePath: '/uploads/license-certificate.pdf',
      uploadedBy: 'Legal Team',
      isActive: true,
      tenantId: '',
      createdAt: new Date('2024-02-01'),
      updatedAt: new Date('2024-02-01')
    },
    {
      id: '3',
      name: 'Insurance Policy.pdf',
      originalName: 'Insurance Policy.pdf',
      type: 'Insurance',
      mimeType: 'application/pdf',
      size: 3355443,
      filePath: '/uploads/insurance-policy.pdf',
      uploadedBy: 'Finance Team',
      isActive: true,
      tenantId: '',
      createdAt: new Date('2024-02-15'),
      updatedAt: new Date('2024-02-15')
    },
    {
      id: '4',
      name: 'Floor Plans.dwg',
      originalName: 'Floor Plans.dwg',
      type: 'Technical',
      mimeType: 'application/octet-stream',
      size: 5872025,
      filePath: '/uploads/floor-plans.dwg',
      uploadedBy: 'Engineering',
      isActive: true,
      tenantId: '',
      createdAt: new Date('2024-03-01'),
      updatedAt: new Date('2024-03-01')
    }
  ];

  constructor(
    private fb: FormBuilder,
    private tenantService: TenantService,
    private tenantDocumentService: TenantDocumentService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService,
    public dialogRef: MatDialogRef<TenantViewModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TenantViewModalData
  ) {
    this.tenantForm = this.fb.group({
      name: ['', [Validators.required]],
      slug: ['', [Validators.required]],
      description: [''],
      contactEmail: ['', [Validators.email]],
      contactPhone: [''],
      city: [''],
      country: [''],
      website: ['']
    });
  }

  ngOnInit(): void {
    if (this.data.tenant) {
      this.tenantForm.patchValue(this.data.tenant);
      this.tenantForm.disable(); // Start in view mode
      this.loadTenantDocuments();
    }
  }

  loadTenantDocuments(): void {
    this.loadingDocuments = true;
    this.tenantDocumentService.getTenantDocuments(this.data.tenant.id).subscribe({
      next: (documents) => {
        this.documents = documents;
        this.loadingDocuments = false;
      },
      error: (error) => {
        console.error('Error loading tenant documents:', error);
        this.toastService.info('Info', 'Loading demo documents for preview');
        // Fallback to mock data
        this.documents = this.mockDocuments;
        this.loadingDocuments = false;
      }
    });
  }

  get dialogTitle(): string {
    return this.data.tenant ? `${this.data.tenant.name} Details` : 'Tenant Details';
  }

  toggleEditMode(): void {
    this.isEditing = !this.isEditing;
    
    if (this.isEditing) {
      this.tenantForm.enable();
    } else {
      this.tenantForm.disable();
      // Reset form to original values
      this.tenantForm.patchValue(this.data.tenant);
    }
  }

  onSave(): void {
    if (this.tenantForm.valid && this.isEditing) {
      this.isLoading = true;
      
      const updateData: UpdateTenantRequest = {
        ...this.tenantForm.value
      };

      this.tenantService.updateTenant(this.data.tenant.id, updateData).subscribe({
        next: () => {
          this.toastService.success('Success', 'Tenant updated successfully');
          this.isLoading = false;
          this.isEditing = false;
          this.tenantForm.disable();
          this.dialogRef.close(true);
        },
        error: (error) => {
          this.toastService.error('Error', 'Failed to update tenant');
          this.isLoading = false;
          console.error('Error updating tenant:', error);
        }
      });
    }
  }

  onCancel(): void {
    if (this.isEditing) {
      this.toggleEditMode(); // Cancel editing
    } else {
      this.dialogRef.close(false);
    }
  }

  onClose(): void {
    this.dialogRef.close(false);
  }

  // Document management methods
  uploadDocument(): void {
    // Create file input element
    const fileInput = window.document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.zip,.rar';
    fileInput.style.display = 'none';
    
    fileInput.onchange = (event: any) => {
      const file = event.target?.files?.[0];
      if (file) {
        this.handleFileUpload(file);
      }
    };
    
    window.document.body.appendChild(fileInput);
    fileInput.click();
    window.document.body.removeChild(fileInput);
  }

  handleFileUpload(file: File): void {
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      this.toastService.error('Error', 'File size must be less than 10MB');
      return;
    }

    this.loadingDocuments = true;
    
    // Determine document type based on file extension
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const documentType = this.getDocumentTypeFromExtension(fileExtension || '');

    this.tenantDocumentService.uploadDocument(this.data.tenant.id, file, documentType).subscribe({
      next: (response: any) => {
        // Handle both old format (direct document) and new format (wrapped response)
        const uploadedDocument = response.data || response;
        this.toastService.success('Success', response.message || `${file.name} uploaded successfully`);
        this.loadTenantDocuments(); // Reload documents
      },
      error: (error) => {
        console.error('Upload error:', error);
        const errorMessage = error?.error?.message || 'Failed to upload document';
        this.toastService.error('Error', errorMessage);
        this.loadingDocuments = false;
      }
    });
  }

  downloadDocument(doc: TenantDocument): void {
    this.tenantDocumentService.downloadDocument(this.data.tenant.id, doc.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = doc.originalName;
        link.click();
        window.URL.revokeObjectURL(url);
        
        this.toastService.success('Download', `${doc.name} downloaded successfully`);
      },
      error: (error) => {
        console.error('Download error:', error);
        this.toastService.error('Error', 'Failed to download document');
      }
    });
  }

  deleteDocument(document: TenantDocument): void {
    this.confirmationService.confirmDelete(document.name, 'document').subscribe({
      next: (confirmed) => {
        if (confirmed) {
          this.tenantDocumentService.deleteDocument(this.data.tenant.id, document.id).subscribe({
            next: () => {
              this.toastService.success('Success', `${document.name} deleted successfully`);
              this.loadTenantDocuments(); // Reload documents
            },
            error: (error) => {
              console.error('Delete error:', error);
              this.toastService.error('Error', 'Failed to delete document');
            }
          });
        }
      }
    });
  }

  getDocumentTypeFromExtension(extension: string): string {
    const typeMap: { [key: string]: string } = {
      'pdf': 'Contract',
      'doc': 'Document',
      'docx': 'Document',
      'xls': 'Spreadsheet', 
      'xlsx': 'Spreadsheet',
      'png': 'Image',
      'jpg': 'Image',
      'jpeg': 'Image',
      'zip': 'Archive',
      'rar': 'Archive',
      'dwg': 'Technical',
      'dxf': 'Technical'
    };
    return typeMap[extension] || 'Other';
  }

  getDocumentIcon(type: string): string {
    return this.tenantDocumentService.getDocumentIcon(type);
  }

  formatFileSize(size: number | string): string {
    if (typeof size === 'string') return size;
    return this.tenantDocumentService.formatFileSize(size);
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) {
      return 'N/A';
    }
    
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if date is valid
    if (!dateObj || isNaN(dateObj.getTime())) {
      return 'N/A';
    }
    
    return dateObj.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }

  onPhoneInput(event: KeyboardEvent | ClipboardEvent): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // For paste events, filter the pasted content
    if (event instanceof ClipboardEvent) {
      const clipboardData = event.clipboardData || (window as any).clipboardData;
      const pastedText = clipboardData.getData('text');
      
      // Only allow digits and phone characters: +, -, spaces, parentheses, periods
      const filtered = pastedText.replace(/[^\d+\-() .]/g, '');
      
      if (filtered !== pastedText) {
        event.preventDefault();
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        value = value.substring(0, start) + filtered + value.substring(end);
        this.tenantForm.get('contactPhone')?.setValue(value, { emitEvent: false });
        setTimeout(() => {
          input.setSelectionRange(start + filtered.length, start + filtered.length);
        }, 0);
      }
      return;
    }

    // For keyboard events, prevent non-phone characters
    if (event instanceof KeyboardEvent) {
      const key = event.key;
      
      // Allow: digits, +, -, spaces, parentheses, periods, and control keys
      const allowedKeys = /[\d+\-() .]|Backspace|Delete|Tab|Escape|Enter|ArrowLeft|ArrowRight|ArrowUp|ArrowDown|Home|End/;
      
      // Allow Ctrl/Cmd combinations (for copy, paste, select all, etc.)
      if (event.ctrlKey || event.metaKey) {
        return;
      }
      
      if (!allowedKeys.test(key)) {
        event.preventDefault();
        return;
      }
    }
  }
}
